// ---------- Governance: nav tree, route matching & formatting helpers ----------
// Shared, pure (no React) helpers for the Cardano Governance Explorer. The
// governance feature reuses the explorer's design system + http client; this
// module only models navigation and formats values that the backend already
// computed (voting power, tallies, treasury flows). No governance math here.
import { formatLovelace } from "@/lib/format";

// Five-column mega-menu / mobile overlay structure.
export const GOV_NAV = [
  {
    heading: "Overview",
    items: [
      { label: "Governance Overview", path: "/governance", desc: "Live state & dashboard", icon: "ChartLineUp" },
      { label: "Protocol Parameters", path: "/governance/parameters", desc: "Current on-chain params", icon: "SlidersHorizontal" },
    ],
  },
  {
    heading: "Voting Bodies",
    items: [
      { label: "Constitutional Committee", path: "/governance/committee", desc: "Members & quorum", icon: "Bank" },
      { label: "DReps", path: "/governance/dreps", desc: "Delegated representatives", icon: "UsersThree" },
      { label: "Stake Pool Voting", path: "/governance/pools", desc: "SPO governance votes", icon: "Stack" },
    ],
  },
  {
    heading: "Actions",
    items: [
      { label: "Live Governance Actions", path: "/governance/proposals/live", desc: "Open for voting now", icon: "Lightning" },
      { label: "All Governance Actions", path: "/governance/proposals", desc: "Full proposal history", icon: "Gavel" },
    ],
  },
  {
    heading: "Treasury",
    items: [
      { label: "ADA Treasury", path: "/governance/treasury", desc: "Balance & flows", icon: "Vault" },
      { label: "Treasury Withdrawals", path: "/governance/withdrawals", desc: "Enacted payouts", icon: "HandCoins" },
    ],
  },
  {
    heading: "Constitution",
    items: [
      { label: "Current Constitution", path: "/governance/constitution", desc: "On-chain text & anchor", icon: "Scroll" },
      { label: "Constitution History", path: "/governance/constitution/history", desc: "Past versions", icon: "ClockCounterClockwise" },
    ],
  },
];

// Flat lookup of every governance destination (used by breadcrumbs/search).
export const GOV_ITEMS = GOV_NAV.flatMap((c) => c.items);

// Ordered route patterns → keys. More specific patterns first.
export const GOV_ROUTES = [
  { key: "overview", pattern: /^\/governance\/?$/ },
  { key: "parameters", pattern: /^\/governance\/parameters\/?$/ },
  { key: "committeeMember", pattern: /^\/governance\/committee\/members\/([^/]+)\/?$/, params: ["coldId"] },
  { key: "committee", pattern: /^\/governance\/committee\/?$/ },
  { key: "dreps", pattern: /^\/governance\/dreps\/?$/ },
  { key: "drep", pattern: /^\/governance\/dreps\/([^/]+)\/?$/, params: ["drepId"] },
  { key: "pools", pattern: /^\/governance\/pools\/?$/ },
  { key: "pool", pattern: /^\/governance\/pools\/([^/]+)\/?$/, params: ["poolId"] },
  { key: "proposalsLive", pattern: /^\/governance\/proposals\/live\/?$/ },
  { key: "proposals", pattern: /^\/governance\/proposals\/?$/ },
  { key: "proposal", pattern: /^\/governance\/proposals\/([^/]+)\/([^/]+)\/?$/, params: ["txHash", "certIndex"] },
  { key: "treasury", pattern: /^\/governance\/treasury\/?$/ },
  { key: "withdrawals", pattern: /^\/governance\/withdrawals\/?$/ },
  { key: "constitutionHistory", pattern: /^\/governance\/constitution\/history\/?$/ },
  { key: "constitution", pattern: /^\/governance\/constitution\/?$/ },
];

export function matchGovRoute(path) {
  const p = (path || "/governance").startsWith("/") ? path : `/${path}`;
  for (const r of GOV_ROUTES) {
    const m = p.match(r.pattern);
    if (m) {
      const params = {};
      (r.params || []).forEach((name, i) => {
        try { params[name] = decodeURIComponent(m[i + 1]); } catch { params[name] = m[i + 1]; }
      });
      return { key: r.key, params };
    }
  }
  return { key: "overview", params: {} };
}

// Human titles + breadcrumb trail per route key.
const CRUMB = (label, path) => ({ label, path });
export function govMeta(key, params = {}) {
  const root = CRUMB("Governance", "/governance");
  switch (key) {
    case "overview":
      return { title: "Governance Overview", crumbs: [root] };
    case "parameters":
      return { title: "Protocol Parameters", crumbs: [root, CRUMB("Parameters", "/governance/parameters")] };
    case "committee":
      return { title: "Constitutional Committee", crumbs: [root, CRUMB("Committee", "/governance/committee")] };
    case "committeeMember":
      return { title: "Committee Member", crumbs: [root, CRUMB("Committee", "/governance/committee"), CRUMB(truncId(params.coldId), null)] };
    case "dreps":
      return { title: "Delegated Representatives", crumbs: [root, CRUMB("DReps", "/governance/dreps")] };
    case "drep":
      return { title: "DRep Profile", crumbs: [root, CRUMB("DReps", "/governance/dreps"), CRUMB(truncId(params.drepId), null)] };
    case "pools":
      return { title: "Stake Pool Voting", crumbs: [root, CRUMB("Pools", "/governance/pools")] };
    case "pool":
      return { title: "Pool Voting", crumbs: [root, CRUMB("Pools", "/governance/pools"), CRUMB(truncId(params.poolId), null)] };
    case "proposalsLive":
      return { title: "Live Governance Actions", crumbs: [root, CRUMB("Actions", "/governance/proposals"), CRUMB("Live", "/governance/proposals/live")] };
    case "proposals":
      return { title: "Governance Actions", crumbs: [root, CRUMB("Actions", "/governance/proposals")] };
    case "proposal":
      return { title: "Governance Action", crumbs: [root, CRUMB("Actions", "/governance/proposals"), CRUMB(truncId(params.txHash), null)] };
    case "treasury":
      return { title: "ADA Treasury", crumbs: [root, CRUMB("Treasury", "/governance/treasury")] };
    case "withdrawals":
      return { title: "Treasury Withdrawals", crumbs: [root, CRUMB("Withdrawals", "/governance/withdrawals")] };
    case "constitution":
      return { title: "Current Constitution", crumbs: [root, CRUMB("Constitution", "/governance/constitution")] };
    case "constitutionHistory":
      return { title: "Constitution History", crumbs: [root, CRUMB("Constitution", "/governance/constitution"), CRUMB("History", "/governance/constitution/history")] };
    default:
      return { title: "Governance", crumbs: [root] };
  }
}

function truncId(s) {
  if (!s) return "—";
  return s.length > 20 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;
}

// ---------- governance value formatting (presentation only) ----------

const GOV_ACTION_LABELS = {
  info_action: "Info Action",
  new_committee: "New Committee",
  treasury_withdrawals: "Treasury Withdrawal",
  parameter_change: "Parameter Change",
  hard_fork_initiation: "Hard Fork",
  no_confidence: "No Confidence",
  new_constitution: "New Constitution / Guardrails",
};

export const govActionLabel = (type) =>
  GOV_ACTION_LABELS[type] ||
  (type ? String(type).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

// Badge tone (matches primitives.jsx Badge tones) for a proposal status.
export const govStatusTone = (status) => {
  switch (status) {
    case "active":
      return "info";
    case "ratified":
    case "enacted":
      return "success";
    case "expired":
      return "default";
    case "dropped":
    case "rejected":
      return "danger";
    default:
      return "default";
  }
};

// Exact ADA string from a lovelace integer-string (BigInt-safe, via format.js).
export const adaFromLovelace = (lovelaceStr) => formatLovelace(lovelaceStr, "ada");

// Numeric ADA for charts/sparklines (precision-lossy — never for exact display).
export const lovelaceToAda = (lovelaceStr) => {
  if (lovelaceStr === null || lovelaceStr === undefined) return 0;
  return Number(lovelaceStr) / 1e6;
};

// Percent from a 0..1 threshold (e.g. 0.67 → "67%").
export const asPct = (frac) => {
  if (frac === null || frac === undefined || Number.isNaN(Number(frac))) return "—";
  return `${(Number(frac) * 100).toFixed(0)}%`;
};

// Finer percent for ratios (e.g. 0.07734629 → "7.73%").
export const asPct2 = (frac) => {
  if (frac === null || frac === undefined || Number.isNaN(Number(frac))) return "—";
  return `${(Number(frac) * 100).toFixed(2)}%`;
};

// Badge tone for a vote.
export const govVoteTone = (v) => (v === "yes" ? "success" : v === "no" ? "danger" : "default");

// ---------- governance identifier detection (main search bar) ----------
// Lets users paste a governance action / DRep / pool id straight into the
// explorer's search bar and jump to the right governance page. No network
// call for detection — purely decodes the bech32 shape locally.

const BECH32_CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";

function convertBits(data, from, to) {
  let acc = 0;
  let bits = 0;
  const ret = [];
  const maxv = (1 << to) - 1;
  for (const value of data) {
    if (value < 0 || value >> from) return null;
    acc = (acc << from) | value;
    bits += from;
    while (bits >= to) {
      bits -= to;
      ret.push((acc >> bits) & maxv);
    }
  }
  return ret;
}

// Minimal bech32 decode (no checksum verification — we only need the payload
// bytes, and the upstream API validates the id anyway).
function bech32Payload(str) {
  const s = (str || "").toLowerCase();
  const pos = s.lastIndexOf("1");
  if (pos < 1) return null;
  const hrp = s.slice(0, pos);
  const dataPart = s.slice(pos + 1);
  if (dataPart.length < 6) return null;
  const data = [];
  for (const ch of dataPart) {
    const idx = BECH32_CHARSET.indexOf(ch);
    if (idx === -1) return null;
    data.push(idx);
  }
  const bytes = convertBits(data.slice(0, data.length - 6), 5, 8);
  if (!bytes) return null;
  return { hrp, bytes };
}

// gov_action1… → { txHash, certIndex }. The 33-byte payload is a 32-byte tx
// hash followed by a single-byte certificate index. The proposal detail API
// resolves by tx hash + cert index (not by the bech32 id directly).
export function decodeGovActionId(id) {
  const dec = bech32Payload(id);
  if (!dec || !dec.hrp.startsWith("gov_action")) return null;
  const b = dec.bytes;
  if (b.length < 33) return null;
  const txHash = b.slice(0, 32).map((x) => x.toString(16).padStart(2, "0")).join("");
  return { txHash, certIndex: b[32] };
}

const DREP_RE = /^drep(_script|_vkh)?1[02-9ac-hj-np-z]+$/i;
const GOV_ACTION_RE = /^gov_action1[02-9ac-hj-np-z]+$/i;
const POOL_RE = /^pool1[02-9ac-hj-np-z]+$/i;

// Auto-routing for the default ("All filters") search: governance actions and
// DReps have unambiguous bech32 prefixes, so they jump to governance even
// without picking the Governance filter. Pools intentionally stay out of here
// (they resolve to the chain pool explorer by default).
export function detectGovTarget(input) {
  const s = (input || "").trim();
  if (!s) return null;
  if (GOV_ACTION_RE.test(s)) {
    const d = decodeGovActionId(s);
    if (d) return { kind: "proposal", path: `/governance/proposals/${d.txHash}/${d.certIndex}` };
  }
  if (DREP_RE.test(s)) return { kind: "drep", path: `/governance/dreps/${s}` };
  return null;
}

// Explicit "Governance" filter: everything (incl. a pool id → its SPO voting
// page) is routed inside the governance explorer.
export function resolveGovSearch(input) {
  const s = (input || "").trim();
  if (!s) return null;
  const t = detectGovTarget(s);
  if (t) return t;
  if (POOL_RE.test(s)) return { kind: "pool", path: `/governance/pools/${s}` };
  return null;
}
